import { Check, Diamond, LockKeyhole, Play, Plus, Share2, X } from 'lucide-react';
import type { Episode, Series } from '../types';

interface SeriesDetailProps {
  series: Series;
  unlockedEpisodeIds: string[];
  progressByEpisodeId: Record<string, number>;
  isInMyList: boolean;
  onClose: () => void;
  onPlayEpisode: (series: Series, episode: Episode) => void;
  onUnlockEpisode: (series: Series, episode: Episode) => void;
  onToggleMyList: (seriesId: string) => void;
}

export function SeriesDetail({
  series,
  unlockedEpisodeIds,
  progressByEpisodeId,
  isInMyList,
  onClose,
  onPlayEpisode,
  onUnlockEpisode,
  onToggleMyList
}: SeriesDetailProps) {
  return (
    <div className="drawer-backdrop" onMouseDown={onClose}>
      <aside
        className="series-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={`${series.title} details`}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button className="drawer-close" onClick={onClose} aria-label="Close series details"><X /></button>
        <div className="drawer-hero">
          <img src={series.heroImage} alt="" />
          <div className="drawer-hero-overlay" />
          <button className="drawer-hero-play" onClick={() => onPlayEpisode(series, series.episodes[0])} aria-label="Play first episode">
            <Play fill="currentColor" />
          </button>
        </div>

        <div className="drawer-content">
          <h2>{series.title}</h2>
          <div className="drawer-meta">{series.genres.join(' • ')} • {series.episodes.length} episodes • {series.year}</div>
          <p>{series.description}</p>
          <div className="drawer-actions">
            <button className="text-action" onClick={() => onToggleMyList(series.id)}>
              {isInMyList ? <Check /> : <Plus />} {isInMyList ? 'In my list' : 'My list'}
            </button>
            <button className="text-action" onClick={() => navigator.clipboard?.writeText(window.location.href)}>
              <Share2 /> Share
            </button>
          </div>

          <div className="episode-heading">
            <h3>Episodes</h3>
            <span>Episodes 1–{series.freeEpisodeCount} are free</span>
          </div>

          <div className="episode-list">
            {series.episodes.map((episode) => {
              const unlocked = episode.free || unlockedEpisodeIds.includes(episode.id);
              const progress = progressByEpisodeId[episode.id] ?? 0;
              return (
                <article className="episode-row" key={episode.id}>
                  <button className="episode-thumbnail" onClick={() => unlocked ? onPlayEpisode(series, episode) : onUnlockEpisode(series, episode)}>
                    <img src={episode.thumbnail} alt="" loading="lazy" />
                    <span className="episode-play-icon">{unlocked ? <Play size={16} fill="currentColor" /> : <LockKeyhole size={16} />}</span>
                    {progress > 0 && <span className="episode-thumb-progress" style={{ width: `${progress * 100}%` }} />}
                  </button>
                  <button className="episode-copy" onClick={() => unlocked ? onPlayEpisode(series, episode) : onUnlockEpisode(series, episode)}>
                    <strong>{episode.number}. {episode.title}</strong>
                    <span>{formatDuration(episode.durationSeconds)}</span>
                  </button>
                  {episode.free ? (
                    <span className="free-badge">Free</span>
                  ) : unlocked ? (
                    <span className="unlocked-label"><Check size={16} /> Unlocked</span>
                  ) : (
                    <button className="episode-cost" onClick={() => onUnlockEpisode(series, episode)}>
                      <Diamond size={15} /> {episode.diamondCost}
                    </button>
                  )}
                </article>
              );
            })}
          </div>
        </div>
      </aside>
    </div>
  );
}

function formatDuration(seconds: number) {
  const minutes = Math.floor(seconds / 60);
  const remaining = seconds % 60;
  return `${minutes}:${String(remaining).padStart(2, '0')}`;
}
